import React from 'react';
import { toast } from 'react-toastify';
import { CheckoutFailureData } from '../../interfaces';
import useUser from './useUser';
import { AppContext } from '../../context/AppContext/context';
import useCalendarSelectedAppointment from '../appointments/useCalendarSelectedAppointment';
import { cancelCurrentTerminalReaderAction } from '../../services/business';
import { cancelCheckout } from '../../services/payments';
import { isAppointmentDepositTerminalCheckoutLoading } from '../../helpers/appointments';
import { cancelDepositCheckout } from '../../services/appointments';
import { SelectedAppointment } from '../../types/appointments';

function useCheckoutForm() {
  const { dispatch, state } = React.useContext(AppContext);
  const {
    terminalCheckoutModal,
    mostRecentPaymentId,
    guestReceiptPaymentId,
    pendingGuestReceipt,
  } = state.checkout;
  const { openedAppointment } = state;

  const { user: loadedUser } = useUser();

  const { setSelectedAppointmentUpdated } = useCalendarSelectedAppointment();

  // Close Checkout modal and unset selected appointment
  const closeCheckout = React.useCallback(() => {
    dispatch({ type: 'SET_SELECTED_APPOINTMENT', payload: null });
    dispatch({ type: 'SET_CHECKOUT_MODAL_OPEN', payload: false });
  }, [dispatch]);

  // Open checkout modal, and if appointment is provided, set it
  const openCheckout = React.useCallback(
    (appointment?: SelectedAppointment) => {
      if (appointment) {
        dispatch({ type: 'SET_SELECTED_APPOINTMENT', payload: appointment });
      }
      dispatch({ type: 'SET_CHECKOUT_MODAL_OPEN', payload: true });
    },
    [dispatch],
  );

  // Reset checkout failure data to null
  const resetCheckoutFailureData = React.useCallback(() => {
    dispatch({ type: 'SET_CHECKOUT_FAILURE_DATA', payload: null });
  }, [dispatch]);

  // Set checkout failure data
  const setCheckoutFailureData = React.useCallback(
    (data: CheckoutFailureData) => {
      dispatch({ type: 'SET_CHECKOUT_FAILURE_DATA', payload: data });
    },
    [dispatch],
  );

  // Set checkout failure data
  const setMostRecentPaymentId = React.useCallback(
    (paymentId: string) => {
      dispatch({ type: 'SET_MOST_RECENT_PAYMENTID', payload: paymentId });
    },
    [dispatch],
  );

  const openGuestReceipt = React.useCallback(
    (paymentId: string) => {
      dispatch({ type: 'SET_GUEST_RECEIPT_PAYMENT_ID', payload: paymentId });
    },
    [dispatch],
  );

  const closeGuestReceipt = React.useCallback(() => {
    dispatch({ type: 'SET_GUEST_RECEIPT_PAYMENT_ID', payload: null });
  }, [dispatch]);

  const setPendingGuestReceipt = React.useCallback(
    (
      payload: {
        paymentId: string;
        splitPaymentsGroupId: string | null;
      } | null,
    ) => {
      dispatch({ type: 'SET_PENDING_GUEST_RECEIPT', payload });
    },
    [dispatch],
  );

  // Set terminal checkout modal to loading
  const setTerminalCheckoutModalToLoading = React.useCallback(() => {
    dispatch({
      type: 'SET_TERMINAL_CHECKOUT_MODAL',
      payload: {
        isOpen: true,
        title: 'Payment Processing',
        message:
          'Charge sent to terminal. Waiting on payment. Please do not refresh the page. Having issues? Cancel the checkout below and try again.',
        buttonText: 'Cancel Checkout',
        status: 'loading',
      },
    });
  }, [dispatch]);

  // Set terminal checkout modal to failed state with failure message
  const setTerminalCheckoutModalToFailed = React.useCallback(
    (failureMessage: string) => {
      dispatch({
        type: 'SET_TERMINAL_CHECKOUT_MODAL',
        payload: {
          isOpen: true,
          title: 'Customer Payment Failed',
          message: failureMessage,
          buttonText: 'OK',
          status: 'failed',
        },
      });
    },
    [dispatch],
  );

  const resetTerminalCheckoutModalState = React.useCallback(() => {
    dispatch({
      type: 'SET_TERMINAL_CHECKOUT_MODAL',
      payload: {
        isOpen: false,
        title: '',
        message: '',
        buttonText: '',
        status: 'idle',
      },
    });
    setMostRecentPaymentId('');
  }, [dispatch, setMostRecentPaymentId]);

  const setTerminalCheckoutModalToSuccess = React.useCallback(
    ({
      paymentId,
      isGuest,
    }: { paymentId?: string; isGuest?: boolean } = {}) => {
      setMostRecentPaymentId('');

      // Guest checkouts jump straight to the receipt prompt — no extra
      // success modal, since the receipt itself is the confirmation.
      if (isGuest && paymentId) {
        resetTerminalCheckoutModalState();
        openGuestReceipt(paymentId);
        return undefined;
      }

      dispatch({
        type: 'SET_TERMINAL_CHECKOUT_MODAL',
        payload: {
          isOpen: true,
          title: 'Checkout Success',
          message: 'Terminal Checkout Success!',
          buttonText: '',
          status: 'success',
        },
      });

      const timeout = setTimeout(resetTerminalCheckoutModalState, 2000);

      return () => clearTimeout(timeout);
    },
    [
      dispatch,
      resetTerminalCheckoutModalState,
      setMostRecentPaymentId,
      openGuestReceipt,
    ],
  );

  const cancelTerminalCheckout = React.useCallback(async () => {
    if (!loadedUser || !loadedUser.business.terminalReaderId) return;

    try {
      if (openedAppointment?.appointmentId) {
        const isDepositTerminalCheckoutLoading =
          isAppointmentDepositTerminalCheckoutLoading(openedAppointment);

        if (isDepositTerminalCheckoutLoading) {
          await cancelDepositCheckout(openedAppointment.appointmentId);
          setSelectedAppointmentUpdated(true);
        } else {
          await cancelCheckout({
            appointmentId: openedAppointment?.appointmentId,
            terminalReaderId: loadedUser.business.terminalReaderId,
          });

          if (!state.checkout.isInSplitCheckout) {
            setSelectedAppointmentUpdated(true);
          }
        }
      } else {
        await cancelCurrentTerminalReaderAction(
          loadedUser.business.id,
          loadedUser.business.terminalReaderId,
        );
      }

      toast.success('Terminal checkout canceled.');
    } catch (cancelError) {
      toast.error('Error canceling terminal checkout');
    }
  }, [
    loadedUser,
    openedAppointment,
    setSelectedAppointmentUpdated,
    state.checkout.isInSplitCheckout,
  ]);

  const closeTerminalCheckoutModal = React.useCallback(async () => {
    if (terminalCheckoutModal.status === 'loading') {
      await cancelTerminalCheckout();
    }
    resetTerminalCheckoutModalState();
  }, [
    cancelTerminalCheckout,
    resetTerminalCheckoutModalState,
    terminalCheckoutModal.status,
  ]);

  return {
    terminalCheckoutModal,
    closeCheckout,
    openCheckout,
    setTerminalCheckoutModalToLoading,
    resetTerminalCheckoutModalState,
    closeTerminalCheckoutModal,
    resetCheckoutFailureData,
    setCheckoutFailureData,
    setTerminalCheckoutModalToFailed,
    setTerminalCheckoutModalToSuccess,
    setMostRecentPaymentId,
    mostRecentPaymentId,
    guestReceiptPaymentId,
    openGuestReceipt,
    closeGuestReceipt,
    pendingGuestReceipt,
    setPendingGuestReceipt,
  };
}

export default useCheckoutForm;
